import { type SelectProps } from 'antd';
import { useMemo } from 'react';
import { type BankOptionsDataFragment } from '../../api/fragments/BankOptionsData';
import { useGetBankOptionsQuery } from '../../api/queries/getBankOptions';
import { type BankIntegrationProvider, type BankFilteringRule } from '../../api/types';
import { useCompany } from '../contexts/CompanyContextManager';
import SelectField, { type SelectFieldProps } from './SelectField';

export type BankSelectFieldProps = Omit<SelectFieldProps, 'children'> & {
    banks?: BankOptionsDataFragment[];
    companyId?: string;
    forceSkipFetch?: boolean;
    moduleId?: string;
    bankIntegrationProviders?: BankIntegrationProvider[];
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.children.toString().toLowerCase().includes(inputValue.toLowerCase());

const BankSelectField = ({
    banks: banksFromProps,
    companyId,
    moduleId,
    bankIntegrationProviders,
    forceSkipFetch = false,
    ...props
}: BankSelectFieldProps) => {
    const company = useCompany(true);
    const filter = useMemo(
        (): BankFilteringRule => ({ companyId: companyId || company?.id, moduleId, bankIntegrationProviders }),
        [company, companyId, moduleId, bankIntegrationProviders]
    );

    const { data, loading } = useGetBankOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter },
        skip: !!banksFromProps || forceSkipFetch,
    });

    const banks = useMemo(() => banksFromProps || data?.banks?.items, [data?.banks?.items, banksFromProps]);

    const options = useMemo(
        () => (banks || []).map(bank => ({ value: bank.id, label: bank.displayName })).filter(Boolean),
        [banks]
    );

    return (
        <SelectField
            {...props}
            filterOption={filterOption}
            loading={!banks && loading}
            optionFilterProp="label"
            options={options}
            showSearch
        />
    );
};

export default BankSelectField;
